/**
 * Formulaire de création/édition d'un projet, destiné à être rendu dans un `Modal`.
 * Le composant ne parle pas au réseau : il collecte les valeurs et délègue à `onSubmit`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BTN_GHOST, BTN_PRIMARY, FORM_LABEL, INPUT, SELECT, TEXTAREA } from '../ui/theme.js';

const STATUS_OPTION_KEYS = [
  { value: 'draft', key: 'projectStatus.draft' },
  { value: 'active', key: 'projectStatus.active' },
  { value: 'on_hold', key: 'projectStatus.onHold' },
  { value: 'done', key: 'projectStatus.done' },
  { value: 'archived', key: 'projectStatus.archived' },
];

// Libellé de champ exiUI (FORM_LABEL, 1.4.0) : le même que Champ/Selecteur et que toutes les
// lignes de formulaire du produit — deux libellés d'une même ligne ont le même style et le même haut.
const LABEL = `${FORM_LABEL} mb-1`;

function emptyValues(teams) {
  return {
    teamId: teams?.[0]?.id ?? '',
    key: '',
    name: '',
    status: 'draft',
    startDate: '',
    endDate: '',
    description: '',
  };
}

export function ProjectForm({
  teams = [],
  project = null,
  onSubmit,
  onCancel,
  submitting = false,
}) {
  const { t } = useTranslation();
  const STATUS_OPTIONS = STATUS_OPTION_KEYS.map((option) => ({
    value: option.value,
    label: t(option.key),
  }));
  const [values, setValues] = useState(() =>
    project
      ? {
          teamId: project.teamId ?? '',
          key: project.key ?? '',
          name: project.name ?? '',
          status: project.status ?? 'draft',
          startDate: project.startDate ?? '',
          endDate: project.endDate ?? '',
          description: project.description ?? '',
        }
      : emptyValues(teams),
  );
  const [error, setError] = useState(null);

  const set = (field) => (event) => setValues((v) => ({ ...v, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!values.teamId) return setError(t('projectForm.errors.teamRequired'));
    if (!values.key.trim()) return setError(t('projectForm.errors.keyRequired'));
    if (!values.name.trim()) return setError(t('projectForm.errors.nameRequired'));
    setError(null);
    try {
      await onSubmit(values);
    } catch (submitError) {
      setError(submitError.message || t('projectForm.errors.saveFailed'));
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div>
        <label className={LABEL} htmlFor="project-team">
          {t('projectForm.team')}
        </label>
        <select id="project-team" className={SELECT} value={values.teamId} onChange={set('teamId')}>
          <option value="" disabled>
            {t('projectForm.chooseTeam')}
          </option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL} htmlFor="project-key">
            {t('projectForm.key')}
          </label>
          <input
            id="project-key"
            className={INPUT}
            value={values.key}
            onChange={set('key')}
            placeholder={t('projectForm.keyPlaceholder')}
            disabled={Boolean(project)}
          />
        </div>
        <div>
          <label className={LABEL} htmlFor="project-status">
            {t('projectForm.status')}
          </label>
          <select
            id="project-status"
            className={SELECT}
            value={values.status}
            onChange={set('status')}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className={LABEL} htmlFor="project-name">
          {t('projectForm.name')}
        </label>
        <input id="project-name" className={INPUT} value={values.name} onChange={set('name')} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL} htmlFor="project-start">
            {t('projectForm.startDate')}
          </label>
          <input
            id="project-start"
            type="date"
            className={INPUT}
            value={values.startDate || ''}
            onChange={set('startDate')}
          />
        </div>
        <div>
          <label className={LABEL} htmlFor="project-end">
            {t('projectForm.endDate')}
          </label>
          <input
            id="project-end"
            type="date"
            className={INPUT}
            value={values.endDate || ''}
            onChange={set('endDate')}
          />
        </div>
      </div>

      <div>
        <label className={LABEL} htmlFor="project-description">
          {t('projectForm.description')}
        </label>
        <textarea
          id="project-description"
          className={`${TEXTAREA} w-full`}
          rows={3}
          value={values.description || ''}
          onChange={set('description')}
        />
      </div>

      {error && <p className="text-sm text-accent-ink">{error}</p>}

      <div className="mt-2 flex justify-end gap-2">
        <button type="button" className={BTN_GHOST} onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className={BTN_PRIMARY} disabled={submitting}>
          {project ? t('projectForm.save') : t('projectForm.create')}
        </button>
      </div>
    </form>
  );
}
